import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setSections,
  setSelectedSection,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addSection,
  updateSection as updateSectionAction,
  removeSection,
  clearSelectedSection,
} from '../../store/slices/chatbotSectionsSlice';
import {
  GET_CHATBOT_SECTIONS_LIST,
  GET_CHATBOT_SECTION,
} from '../../graphql/queries/chatbotSections.queries';
import {
  CREATE_CHATBOT_SECTION,
  UPDATE_CHATBOT_SECTION,
  DELETE_CHATBOT_SECTION,
} from '../../graphql/mutations/chatbotSections.mutations';
import {
  FindChatbotSectionListInput,
  FindChatbotSectionInput,
  CreateChatbotSectionInput,
  UpdateChatbotSectionInput,
  DeleteChatbotSectionInput,
  ChatbotSectionListResponse,
  ChatbotSectionResponse,
  ChatbotSectionMutationResponse,
  DeleteChatbotSectionResponse,
  ChatbotSection,
} from '../../types/chatbotSections.types';
import ViewModal from '../../components/ChatbotSections/ViewModal';
import CreateEditModal, { ChatbotSectionFormData } from '../../components/ChatbotSections/CreateEditModal';
import ConfirmDeleteDialog from '../../components/ChatbotSections/ConfirmDeleteDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './ChatbotSections.css';

const ChatbotSections: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sections, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.chatbotSections
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<ChatbotSection | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchSections, { loading: fetchingList }] = useLazyQuery<
    { chatbotSections: ChatbotSectionListResponse },
    { params: FindChatbotSectionListInput }
  >(GET_CHATBOT_SECTIONS_LIST, { fetchPolicy: 'network-only' });

  const [fetchSection] = useLazyQuery<
    { chatbotSection: ChatbotSectionResponse },
    { params: FindChatbotSectionInput }
  >(GET_CHATBOT_SECTION, { fetchPolicy: 'network-only' });

  const [createSectionMutation] = useMutation<
    { createChatbotSection: ChatbotSectionMutationResponse },
    { params: CreateChatbotSectionInput }
  >(CREATE_CHATBOT_SECTION);

  const [updateSectionMutation] = useMutation<
    { updateChatbotSection: ChatbotSectionMutationResponse },
    { params: UpdateChatbotSectionInput }
  >(UPDATE_CHATBOT_SECTION);

  const [deleteSectionMutation] = useMutation<
    { deleteChatbotSection: DeleteChatbotSectionResponse },
    { params: DeleteChatbotSectionInput }
  >(DELETE_CHATBOT_SECTION);

  useEffect(() => {
    loadSections();
  }, [pagination.page, pagination.pageSize, search, sort]);

  const loadSections = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSections({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.chatbotSections?.success && data.chatbotSections.data) {
        dispatch(setSections(data.chatbotSections.data));
      } else {
        dispatch(setError(data?.chatbotSections?.message || 'Error al cargar secciones'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'asc' }));
    } else {
      dispatch(setSort({ field: 'createdAt', order: 'desc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSection({ variables: { params: { ids: [id] } } });
      if (data?.chatbotSection?.success && data.chatbotSection.data && data.chatbotSection.data.length > 0) {
        dispatch(setSelectedSection(data.chatbotSection.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.chatbotSection?.message || 'Error al cargar sección'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchSection({ variables: { params: { ids: [id] } } });
      if (data?.chatbotSection?.success && data.chatbotSection.data && data.chatbotSection.data.length > 0) {
        dispatch(setSelectedSection(data.chatbotSection.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.chatbotSection?.message || 'Error al cargar sección'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedSection());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: ChatbotSectionFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateSectionMutation({
          variables: {
            params: {
              id: formData.id,
              code: formData.code,
              name: formData.name,
              description: formData.description,
            },
          },
        });

        if (responseData?.updateChatbotSection?.success) {
          if (responseData.updateChatbotSection.data) {
            dispatch(updateSectionAction(responseData.updateChatbotSection.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSection());
          setSaveError(null);
          setSuccessMessage('Sección actualizada exitosamente');
        } else {
          const msg = responseData?.updateChatbotSection?.message || 'Error al actualizar sección';
          const errs = responseData?.updateChatbotSection?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createSectionMutation({
          variables: {
            params: {
              code: formData.code,
              name: formData.name,
              description: formData.description,
            },
          },
        });

        if (responseData?.createChatbotSection?.success) {
          if (responseData.createChatbotSection.data) {
            dispatch(addSection(responseData.createChatbotSection.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedSection());
          setSaveError(null);
          setSuccessMessage('Sección creada exitosamente');
        } else {
          const msg = responseData?.createChatbotSection?.message || 'Error al crear sección';
          const errs = responseData?.createChatbotSection?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (section: ChatbotSection) => {
    setSectionToDelete(section);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sectionToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteSectionMutation({
        variables: {
          params: {
            ids: [sectionToDelete.id],
          },
        },
      });

      if (responseData?.deleteChatbotSection?.success) {
        dispatch(removeSection(sectionToDelete.id));
        setDeleteDialogOpen(false);
        setSectionToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Sección eliminada exitosamente');
      } else {
        const msg = responseData?.deleteChatbotSection?.message || 'Error al eliminar sección';
        const errs = responseData?.deleteChatbotSection?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedSection());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedSection());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSectionToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<ChatbotSection>[] = [
    {
      field: 'code',
      headerName: 'Código',
      width: 160,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{ color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/chatbot-sections/${params.row.id}/configuration`)}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{ color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/chatbot-sections/${params.row.id}/configuration`)}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 1,
      minWidth: 250,
      valueGetter: (value: string | null) => value || '',
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 140 },
    { field: 'updatedAt', headerName: 'Fecha Actualización', width: 160 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="chatbot-sections-container">
        <Paper elevation={2} className="chatbot-sections-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Secciones Chatbot</Typography>
          </Breadcrumbs>

          <Box className="chatbot-sections-header">
            <Typography variant="h4" component="h1">
              Secciones Chatbot
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de secciones del chatbot
            </Typography>
          </Box>

          <Box className="chatbot-sections-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar sección..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Sección
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={sections}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditModal
          open={createEditModalOpen}
          isEdit={isEdit}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          sectionName={sectionToDelete?.name || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default ChatbotSections;
